import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, SelectPicker} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import { EventRecord, EventRecordProviderList, UpsertEventRecord } from '@/models/event-record.model';
import { getEventRecordByDistanceId, upsertEventRecord } from '@/services/event-record.service';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import ImageUploader from '@/components/ImageUploader';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import UrlInputGroup from '@/components/UrlInputGroup';

const schema = Schema.Model<UpsertEventRecord>({
  id: Schema.Types.NumberType(),
  provider: Schema.Types.NumberType().isRequired('This field is required.'),
  providerUrl: Schema.Types.StringType().isRequired('This field is required.'),
  bibImageUrl: Schema.Types.StringType(),
  runEventDistanceId: Schema.Types.NumberType(),
});

const emptyModel: UpsertEventRecord = {
  id: undefined,
  provider: undefined,
  providerUrl: '',
  bibImageUrl: '',
  runEventDistanceId: undefined
};

interface EventRecordFormProps{
  runEventDistanceId?: number;
};

const EventRecordForm: React.FC<EventRecordFormProps> = ({runEventDistanceId}) => {
  const [formValue, setFormValue] = useState<UpsertEventRecord>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    fetchData();
  }, [runEventDistanceId]);
  
  const fetchData = async () => {
    if (runEventDistanceId) {
      setLoading(true);
      try {
        const result: BaseResponseModel<EventRecord> = await getEventRecordByDistanceId(runEventDistanceId);
        if (result.success) {
          setFormValue(result.result);
        }
      } catch (err) {
        const error = err as AxiosError<BaseErrorModel>;
        if(error.status != 404){
          const errorModel = error.response?.data;
          toastErrorMessage(`Error in fetching Event Record: ${errorModel?.errorMessage}`);
        }
      }
      finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const castedModel = {...formValue,
        runEventDistanceId: runEventDistanceId
      };
      const result: BaseResponseModel<any> = await upsertEventRecord(castedModel);
      if (result.success) {
        toastSuccessMessage('Event record successfully submitted.');
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <PageContent>
      <Form name='upsert-event-record' fluid ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={schema}>
        <Grid w='100%' fluid>
          <Row>
            <Col span={{xs:24, lg:8}}>
              <Form.Group>
                <Form.Label style={{ textAlign: 'center'}}>BIB Image</Form.Label>
                <Form.Control
                  accepter={ImageUploader}
                  name="bibImageUrl"
                  height="200px"
                  url={formValue.bibImageUrl}
                  objectStorageCategory={ObjectStorageFileCategory.EventRecord}
                  onChange={url => setFormValue(pre => ({...pre, bibImageUrl: url}))}              
                />
              </Form.Group>
            </Col>
            <Col span={{xs:24, lg:16}}>
              <Row>
                  <Col span={{xs:24}}>
                    <Form.Group>
                      <Form.Label>Record Provider</Form.Label>
                      <Form.Control name="provider" block
                        accepter={SelectPicker} 
                        data={EventRecordProviderList}
                        searchable={false}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col span={{xs:24}}>
                    <Form.Group>
                      <Form.Label>Provider Url</Form.Label>
                      <Form.Control
                        name="providerUrl"
                        accepter={UrlInputGroup}
                      />
                    </Form.Group>
                  </Col>
                </Row>
            </Col>
            {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
          </Row>
        </Grid>
      </Form>
      <Divider />
      
      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default EventRecordForm;